import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { alpha, Box, Card, Fab, IconButton, InputAdornment, Paper, styled, TextField, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { Call, Mail, Note, WhatsApp } from '@mui/icons-material';
import FullscreenIcon from '@mui/icons-material/OpenInFull';
import CloseIcon from '@mui/icons-material/Close';

import { useWhatsappClient } from '@frontend/features/whatsapp/components/WhatsappClientContext';
import { useTwilio } from '@frontend/features/communications/TwilioContextProvider/provider';
import { ClientData } from '@frontend/features/calls/components/OutgoingCallModal/types';
import { Channels } from '@bandido/shared/constants';

import { useDraft } from '../../context/DraftContext';
import { useEditor } from '../../hooks/useEditor';
import { CaseContentFilterBar } from '../CaseContentFilterBar';
import { CaseContentHistory } from '../CaseContentHistory';
import { NoteEditor } from '../NoteEditor';

const StyledFab = styled(Fab)<{ selected?: boolean }>(({ theme, selected }) => ({
  background: selected ? alpha(theme.palette.primary.main, 0.7) : theme.palette.primary.main,
  border: selected ? `1px solid ${theme.palette.primary.main}` : 'none',
}));

interface CaseContentProps {
  clientData: ClientData
}

export const CaseContent = ({ clientData }: CaseContentProps) => {
  const { t } = useTranslation();
  const { openWhatsapp } = useWhatsappClient();
  const { handleOpenOutgoingCallModal } = useTwilio();
  const { draftPreview, removeDraft, draft } = useDraft();
  const { id: caseId } = useParams();

  const [filterTab, setFilterChannel] = useState<Channels | null>(null);

  const { isOpen, setIsOpen, setChannel, setEditorContent, channel } = useEditor();
  const toggleisOpen = () => setIsOpen((prev) => !prev);

  const handleChangeFilterChannel = (value: Channels | null) => {
    setFilterChannel(value);
  };

  const handleChangeEditorChannel = (value: Channels) => () => {
    if (value === Channels.WHATSAPP) {
      if (clientData.clientWhatsappNumber && clientData.clientId) {
        openWhatsapp(clientData.clientId);
      }
      return;
    }

    if (value === Channels.PHONE) {
      return handleOpenOutgoingCallModal(Number(caseId), clientData);
    }

    setIsOpen(true);
    setEditorContent('');
    setChannel(value);
  };

  const handleOpenEditor = () => {
    setIsOpen(true);
    setEditorContent('');
  };

  const handleCloseEditor = () => {
    setIsOpen(false);
    setChannel(null);

    if (!channel) return;

    removeDraft(channel);
  };

  useEffect(() => {
    let draftChannel: Channels.EMAIL | Channels.NOTES | null = null;
    let draftContent = null;

    const keys = Object.keys(draft);
    keys.forEach((key) => {
      if (draft[key as Channels.EMAIL | Channels.NOTES] && !draftChannel) {
        draftChannel = key as Channels.EMAIL | Channels.NOTES;
        draftContent = draft[key as Channels.EMAIL | Channels.NOTES];
      }
    });

    if (draftChannel && draftContent) {
      setEditorContent(draftContent);
    }
  }, []);

  const draftText = useMemo(() => {
    if (!channel) return '';
    const channelDraft = draftPreview(channel as Channels.EMAIL | Channels.NOTES);

    if (channelDraft) return channelDraft;

    return '';
  }, [channel, draftPreview]);

  const newMessageText = useMemo(
    () => channel && t(`cases.view.content.new${channel?.charAt(0).toUpperCase() + channel.slice(1).toLowerCase()}`),
    [channel],
  );

  return (
    <Card sx={{ minHeight: 'calc(100% - 64px)', width: '100%', borderRadius: 3, boxShadow: 'none' }}>
      <Box display="flex" flexDirection="column" gap={3} padding={3}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexDirection="row"
          sx={{
            boxSizing: 'border-box',
          }}
        >
          <Typography variant="h6">{t('cases.view.content.activity')}</Typography>
          <Box display="flex" gap={1}>
            <StyledFab
              selected={channel === Channels.EMAIL}
              onClick={handleChangeEditorChannel(Channels.EMAIL)}
              size="small"
              color="primary"
            >
              <Mail />
            </StyledFab>
            <StyledFab
              selected={channel === Channels.WHATSAPP}
              onClick={handleChangeEditorChannel(Channels.WHATSAPP)}
              disabled={!clientData.clientWhatsappNumber}
              size="small"
              color="primary"
            >
              <WhatsApp />
            </StyledFab>
            <StyledFab
              selected={channel === Channels.NOTES}
              onClick={handleChangeEditorChannel(Channels.NOTES)}
              size="small"
              color="primary"
            >
              <Note />
            </StyledFab>
            <StyledFab
              selected={channel === Channels.PHONE}
              onClick={handleChangeEditorChannel(Channels.PHONE)}
              size="small"
              color="primary"
            >
              <Call />
            </StyledFab>
          </Box>
        </Box>
        <Box
          sx={{
            boxSizing: 'border-box',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box>
            <TextField
              fullWidth
              placeholder={t('cases.view.content.search')}
              size="small"
              sx={{ minWidth: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <CaseContentFilterBar filterTab={filterTab} handleChangeFilterTab={handleChangeFilterChannel} />
        </Box>
      </Box>
      <CaseContentHistory channel={filterTab} />
      {isOpen && channel ? (
        <NoteEditor onMinimize={toggleisOpen} onClose={handleCloseEditor} />
      ) : channel ? (
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            right: 88,
          }}
        >
          <Paper
            elevation={8}
            onClick={handleOpenEditor}
            sx={{
              display: 'flex',
              cursor: 'pointer',
              alignItems: 'center',
              minWidth: 370,
              justifyContent: 'space-between',
              borderRadius: '16px 16px 0 0',
              backgroundColor: (theme) => theme.palette.primaryAlternative.lighter,
              padding: (theme) => theme.spacing(0.5, 1),
            }}
          >
            <Typography padding={(theme) => theme.spacing(1, 2)} variant="subtitle2" color="text.primary">
              {newMessageText}
            </Typography>
            {draftText && (
              <Box display="flex" alignItems="center" gap={0.5}>
                <Typography variant="subtitle2" color="text.secondary">
                  {t('cases.view.content.draft')}:
                </Typography>
                <Typography
                  sx={{
                    whiteSpace: 'nowrap',
                    wordBreak: 'break-word',
                    maxWidth: '100px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                  variant="subtitle2"
                  color="text.primary"
                >
                  {draftText}
                </Typography>
              </Box>
            )}
            <Box>
              <IconButton size="small" onClick={handleOpenEditor} color="primary">
                <FullscreenIcon fontSize="small" />
              </IconButton>

              <IconButton size="small" onClick={() => handleCloseEditor()} color="primary">
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Paper>
        </Box>
      ) : null}
    </Card>
  );
};
